import {memo, useCallback, useMemo, useState} from "react";
import {Alert, Button, Card, Col, Divider, Row, Skeleton, Space, Switch} from "antd";
import SanKey from "../Charts/SanKey";
import SkillsBarChart from "../Charts/SkillsBarChart";
import GrowthRateChart from "../Charts/GrowthRateChart";
import IndustryChart from "../Charts/IndustryChart";
import useSWR from "swr";
import UniversityChart from "../Charts/UniversityChart";
import CompareRadarChart from "../Charts/CompareRadarChart";
import pluck from "../Helpers/ArrayHelper";
import CareerDrawer from "./CareerDrawer";
import ColumnCompareAbleChart from "../Charts/ColumnCompareChart";

const SubGroupShowcase = memo(({groupId, subGroupId}) => {

    const {data: subGroup, error} = useSWR(`/groups/${groupId}/${subGroupId}`)

    const [isOpenCareerDrawer, setIsOpenCareerDrawer] = useState(false);
    const [selectedCareerId, setSelectedCareerId] = useState(0);

    const [isSankeySelectedJobPosts, setIsSankeySelectedJobPosts] = useState(false);
    const [isCompareChartSelectedJobPosts, setIsCompareChartSelectedJobPosts] = useState(false);

    const openDrawerHandler = useCallback((careerId) => {
        if(setIsOpenCareerDrawer && setSelectedCareerId)
            setSelectedCareerId(careerId);
        setIsOpenCareerDrawer(true);
    }, [setIsOpenCareerDrawer, setSelectedCareerId])

    const closeDrawerHandler = useCallback(() => {
        setIsOpenCareerDrawer(false);
    }, [setIsOpenCareerDrawer])

    if(error)
        return <Alert
            message="เกิดข้อผิดพลาด"
            description={`ไม่สามารถดาวน์โหลดข้อมูลได้ กรุณาลองอีกครั้งภายหลัง : ${error}`}
            type="error"
            showIcon
        />

    if(!subGroup)
        return <>
            <Skeleton active size={'large'} />
            <Skeleton active size={'large'} />
            <Skeleton active size={'large'} />
        </>

    return <>
        {
            (!subGroup?.thailand_skills_overview?.length > 0 ||
                !subGroup?.thailand_common_skills_overview?.length > 0 ||
                !subGroup?.thailand_skills_growth_overview?.length > 0 ||
                !subGroup?.thailand_industries_overview?.length > 0 ||
                !subGroup?.thailand_universities_overview?.length > 0) &&
            <Alert
                type='warning'
                message='ไม่พบข้อมูลกลุ่มตัวอย่างของตำแหน่งงานในประเทศไทย'
                description={`ไม่พบบางข้อมูลของกลุ่มตัวอย่างเกี่ยวกับกลุ่มงาน ${subGroup?.name} ในประเทศไทย`}
                style={{ marginTop: '12px', marginBottom: '12px' }}
                showIcon
            />
        }
        <h1>
            กลุ่มย่อย { subGroup?.name }
        </h1>
        <h2>
            ข้อมูลตำแหน่งงานในกลุ่มย่อย { subGroup?.name }
        </h2>
        <Space wrap>
            { subGroup?.careers?.length ? '' : <>
                ไม่มีอาชีพให้แสดง
            </> }
            { subGroup?.careers.map((career) => <>
                    <Button
                        size={'large'}
                        type={'dashed'}
                        onClick={() => {
                            setSelectedCareerId(career?.id);
                            setIsOpenCareerDrawer(true);
                        }}
                    >
                        { career?.name }
                    </Button>
                </>
            ) }
        </Space>
        <Divider/>
        <Row justify={'center'} gutter={[8, 6]}>
            <Col span={24} md={12} lg={6}>
                <Card title="จำนวนกลุ่มตัวอย่างผู้ที่ประกอบอาชีพในกลุ่มนี้" style={{ textAlign: 'center' }} bodyStyle={{ padding: '4px' }}>
                    <span style={{ fontSize: '32px' }}>
                        { subGroup?.counts?.sample.toLocaleString() ?? '-' }
                    </span> คน
                </Card>
            </Col>
            <Col span={24} md={12} lg={6}>
                <Card title="จำนวนกลุ่มตัวอย่างผู้ที่ประกอบอาชีพในกลุ่มนี้ (ไทย)" style={{ textAlign: 'center' }} bodyStyle={{ padding: '4px' }}>
                    <span style={{ fontSize: '32px' }}>
                        { subGroup?.counts?.sample_th.toLocaleString() ?? '-' }
                    </span> คน
                </Card>
            </Col>
            <Col span={24} md={12} lg={6}>
                <Card title="จำนวนประกาศรับสมัครงาน" style={{ textAlign: 'center' }} bodyStyle={{ padding: '4px' }}>
                    <span style={{ fontSize: '32px' }}>
                        { subGroup?.counts?.job_posts_sample.toLocaleString() ?? '-' }
                    </span> ประกาศ
                </Card>
            </Col>
            <Col span={24} md={12} lg={6}>
                <Card title="จำนวนประกาศรับสมัครงาน (ไทย)" style={{ textAlign: 'center' }} bodyStyle={{ padding: '4px' }}>
                    <span style={{ fontSize: '32px' }}>
                        { subGroup?.counts?.job_posts_sample_th.toLocaleString() ?? '-' }
                    </span> ประกาศ
                </Card>
            </Col>
        </Row>
        <h2>
            ภาพรวม Skill
        </h2>
        <Row gutter={8}>
            <Col span={24}>
                <Card>
                    <Row justify={'space-between'}>
                        <Col>
                            <h2>
                                Skill
                            </h2>
                        </Col>
                        <Col>
                            <h2>
                                อาชีพ
                            </h2>
                        </Col>
                    </Row>
                    <SanKey
                        data={subGroup?.skills_overview}
                        source={'skill'}
                        target={'group'}
                        weight={'amount'}
                    />
                </Card>
            </Col>
        </Row>
        <Divider/>
        <h2>
            เปรียบเทียบ Skill
        </h2>
        <Row gutter={8}>
            <Col span={24} lg={!!subGroup?.thailand_common_skills_overview.length ? 12 : 24}>
                <Card
                    title={`จำนวนคนที่มีความสามารถด้านต่าง ๆ จากกลุ่ม ${subGroup?.name} (ทั่วโลก)`}
                >
                    <SkillsBarChart
                        data={subGroup?.common_skills_overview}
                        xAxis={'skill'}
                        yAxis={'amount'}
                        customConfig={{
                            meta: {
                                amount: { alias: 'จำนวนคน', formatter: (v) => `${v} คน` },
                            },
                            color: '#164176'
                        }}
                    />
                </Card>
            </Col>
            {
                !!subGroup?.thailand_common_skills_overview.length && <>

                    <Col span={24} lg={12}>
                        <Card
                            title={`จำนวนคนที่มีความสามารถด้านต่าง ๆ จากกลุ่ม ${subGroup?.name} (ประเทศไทย)`}
                        >
                            <SkillsBarChart
                                data={subGroup?.thailand_common_skills_overview}
                                xAxis={'skill'}
                                yAxis={'amount'}
                                customConfig={{
                                    meta: {
                                        amount: { alias: 'จำนวนคน', formatter: (v) => `${v} คน` },
                                    },
                                    color: '#ff9318'
                                }}
                            />
                        </Card>
                    </Col>

                </>
            }
        </Row>
        {
            subGroup?.thailand_job_posts_overview.length ? <>
                <Row gutter={8}>
                    <Col span={24} lg={subGroup?.thailand_job_posts_overview.length > 0 ? 12 : 24}>
                        <Card
                            title={`จำนวนประกาศรับสมัครงานที่ต้องการทักษะต่าง ๆ (ทั่วโลก)`}
                        >
                            <SkillsBarChart
                                data={subGroup?.job_posts_overview}
                                xAxis={'skill'}
                                yAxis={'amount'}
                                customConfig={{
                                    meta: {amount: {alias: 'จำนวนคน', formatter: (v) => `${v} คน`}},
                                    color: '#164176'
                                }}
                            />
                        </Card>
                    </Col>
                    {
                        subGroup?.thailand_job_posts_overview.length > 0 && <>
                            <Col span={24} lg={12}>
                                <Card
                                    title={`จำนวนประกาศรับสมัครงานที่ต้องการทักษะต่าง ๆ (ประเทศไทย)`}
                                >
                                    <SkillsBarChart
                                        data={subGroup?.thailand_job_posts_overview}
                                        xAxis={'skill'}
                                        yAxis={'amount'}
                                        customConfig={{
                                            meta: {
                                                amount: { alias: 'จำนวนคน', formatter: (v) => `${v} คน` },
                                            },
                                            color: '#ff9318'
                                        }}
                                    />
                                </Card>
                            </Col>
                        </>
                    }
                </Row>
            </> : null
        }
        {
            !!subGroup?.thailand_common_skills_overview.length && <>

                <Divider/>
                <h3>
                    { isCompareChartSelectedJobPosts ? `เปรียบเทียบอัตราส่วนทักษะต่าง ๆ ที่อยู่ในประกาศรับสมัครงาน ในกลุ่ม ${subGroup?.name} ระหว่างทั่วโลก กับ ประเทศไทย`
                        : `เปรียบเทียบอัตราส่วนผู้ที่มีทักษะต่าง ๆ ในกลุ่ม ${subGroup?.name} ระหว่างทั่วโลก กับ ประเทศไทย` }
                </h3>
                {
                    subGroup?.job_posts_overview?.length > 0 ? <>
                        <Row justify={'end'} style={{marginTop: '6px', marginBottom: '6px'}}>
                            <Col>
                                ที่มาข้อมูล : <Switch
                                checkedChildren={'ประกาศรับสมัครงาน'}
                                unCheckedChildren={'คนในตำแหน่งงาน'}
                                defaultChecked={isCompareChartSelectedJobPosts}
                                onChange={(isChecked) => {
                                    setIsCompareChartSelectedJobPosts(isChecked)
                                }}
                            />
                            </Col>
                        </Row>
                    </> : ''
                }
                <Row gutter={8}>
                    <Col span={24} lg={24}>
                        <Card >
                            <ColumnCompareAbleChart
                                data={[
                                    ...(isCompareChartSelectedJobPosts ? subGroup?.job_posts_overview : subGroup?.common_skills_overview).map((skill) => {
                                        return {
                                            skill: skill.skill,
                                            amount: skill.percentage,
                                            scope: 'ทั่วโลก'
                                        };
                                    }),
                                    ...(isCompareChartSelectedJobPosts ? subGroup?.thailand_job_posts_overview : subGroup?.thailand_common_skills_overview).map((skill) => {
                                        return {
                                            skill: skill.skill,
                                            amount: skill.percentage,
                                            scope: 'ประเทศไทย'
                                        };
                                    }),
                                ]}
                                xAxis={'skill'}
                                yAxis={'amount'}
                                customConfig={{
                                    // label: {
                                    //     content: (_ref) => {
                                    //         return `${_ref.amount} %`
                                    //     }
                                    // },
                                    seriesField: 'scope',
                                    meta: {
                                        amount: { alias: 'ร้อยละ', formatter: (v) => `${v}%` }
                                    },
                                    isGroup: true,
                                    // isPercent: true,
                                    scrollbar: { type: 'horizontal' },
                                    color: ['#164176', '#dd720b']
                                }}
                            />
                        </Card>
                    </Col>
                </Row>
                <h3>
                    { isCompareChartSelectedJobPosts ? 'Radar chart เพื่อเปรียบเทียบอัตราส่วนร้อยละของความต้องการทักษะต่าง ๆ ในประกาศรับสมัครงาน ของกลุ่มคนระดับโลก เทียบกับกลุ่มคนระดับประเทศ'
                        : 'Radar chart เพื่อเปรียบเทียบอัตราส่วนร้อยละ ของคนที่มีทักษะต่าง ๆ ของกลุ่มคนระดับโลก เทียบกับกลุ่มคนระดับประเทศ' }
                </h3>
                <Row>
                    <Col span={24} lg={24}>
                        <Card>
                            <CompareRadarChart
                                data={
                                    (() => {
                                        let all_skills = isCompareChartSelectedJobPosts ?
                                            [...new Set(pluck([...subGroup?.job_posts_overview, ...subGroup?.thailand_job_posts_overview], 'skill'))]
                                            : [...new Set(pluck([...subGroup?.common_skills_overview, ...subGroup?.thailand_common_skills_overview], 'skill'))]
                                        let plot_data = [];
                                        all_skills.forEach((skill) => {
                                            let a = (isCompareChartSelectedJobPosts ? subGroup?.job_posts_overview : subGroup?.common_skills_overview).filter(s_skill => s_skill.skill === skill)
                                            let b = (isCompareChartSelectedJobPosts ? subGroup?.thailand_job_posts_overview : subGroup?.thailand_common_skills_overview).filter(s_skill => s_skill.skill === skill)
                                            if(a.length > 0 && b.length > 0) {
                                                a = a[0];
                                                b = b[0];
                                                plot_data.push({
                                                    skill: a.skill,
                                                    percentage: a.percentage,
                                                    scope: 'ทั่วโลก'
                                                });
                                                plot_data.push({
                                                    skill: b.skill,
                                                    percentage: b.percentage,
                                                    scope: 'ประเทศไทย'
                                                });
                                            }
                                        })
                                        // console.log(all_skills)
                                        // console.log(plot_data.splice(0, 20))
                                        return plot_data.splice(0, 20)
                                    })()
                                }
                            />
                        </Card>
                    </Col>
                </Row>

            </>
        }
        <Divider/>
        <h1>
            ข้อมูลเพิ่มเติม
        </h1>
        <h2>
            ทั่วโลก
        </h2>
        <Row gutter={8}>
            <Col span={24} lg={subGroup?.industries_job_posts_overview?.length > 0 ? 24 : 12}>
                <Card
                    title={`ทักษะในกลุ่ม ${subGroup?.name} ที่มีอัตราการเพิ่มมากที่สุดในช่วง 1 ปี`}
                >
                    <GrowthRateChart
                        data={subGroup?.skills_growth_overview}
                    />
                </Card>
            </Col>
            <Col span={24} lg={12}>
                <Card
                    title={`กลุ่มอุตสาหกรรมที่มีตำแหน่งงานมากที่สุดในกลุ่ม ${subGroup?.name}`}
                >
                    <IndustryChart data={subGroup?.industries_overview} />
                </Card>
            </Col>
            {
                subGroup?.industries_job_posts_overview?.length > 0 ?
                    <>
                        <Col span={24} lg={12}>
                            <Card
                                title={`กลุ่มอุตสาหกรรมที่มีประกาศรับสมัครงานในตำแหน่ง ${subGroup?.name}`}
                            >
                                <IndustryChart data={subGroup?.industries_job_posts_overview} />
                            </Card>
                        </Col>
                    </> : null
            }
        </Row>
        <Divider/>
        {
            (!!subGroup?.thailand_skills_growth_overview.length ||
                !!subGroup?.thailand_industries_overview.length ||
                !!subGroup?.thailand_universities_overview.length) && <>
                <h2>
                    ประเทศไทย
                </h2>
                <Row gutter={8}>
                    <Col span={24} lg={12}>
                        <Card
                            title={`ทักษะในกลุ่ม ${subGroup?.name} ที่มีอัตราการเพิ่มมากที่สุดในช่วง 1 ปี`}
                        >
                            <GrowthRateChart
                                data={subGroup?.thailand_skills_growth_overview}
                            />
                        </Card>
                    </Col>
                    <Col span={24} lg={12}>
                        <Card
                            title={`กลุ่มอุตสาหกรรมที่มีตำแหน่งงานในกลุ่ม ${subGroup?.name}`}
                        >
                            <IndustryChart
                                data={subGroup?.thailand_industries_overview}
                            />
                        </Card>
                    </Col>
                </Row>
                <Row style={{ marginTop: '24px' }}>
                    {
                        subGroup?.thailand_industries_job_posts_overview?.length > 0 ?
                            <>
                                <Col span={24} lg={12}>
                                    <Card
                                        title={`กลุ่มอุตสาหกรรมที่มีประกาศรับสมัครงานในตำแหน่ง ${subGroup?.name}`}
                                    >
                                        <IndustryChart data={subGroup?.thailand_industries_job_posts_overview} />
                                    </Card>
                                </Col>
                            </> : null
                    }
                    <Col span={subGroup?.thailand_industries_job_posts_overview?.length > 0 ? 12 : 24}>
                        <Card
                            title={`มหาวิทยาลัยที่มีการผลิตบุคลากร เพื่อประกอบอาชีพในกลุ่ม ${subGroup?.name}`}
                        >
                            <UniversityChart data={subGroup?.thailand_universities_overview} />
                        </Card>
                    </Col>
                </Row>
            </>
        }

        <CareerDrawer
            careerId={selectedCareerId}
            visible={isOpenCareerDrawer}
            onClose={() => closeDrawerHandler}
        />
    </>
});

export default SubGroupShowcase;
